/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, GetArrow, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Scale,
    Tick,
    Chart,
    ChartEvent,
    ActiveElement,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            padding: '0 15px',
            justifyContent: 'center',
            paddingBottom: '10px',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        barTitleContainer: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: '15px 15px 0px 15px',
            fontSize: '16px',
            fontWeight: 600,
            [`& .${`h1`}`]: {
                display: 'flex',
                alignItems: 'center',
            },
            [`& .${`h2`}`]: {
                fontSize: '2.5rem',
                padding: '10px 0',
            },
            [`& .${`h3`}`]: {
                paddingTop: '10px',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            [`& .${`titleWrapper`}`]: {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                [`& .${`title`}`]: {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`value`}`]: {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`secondary-title`}`]: {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
    }),
);

export enum LastCountEnum {
    LAST_WEEK,
    LAST_14_DAYS,
    LAST_30_DAYS,
    LAST_12_MOTNHS,
}

interface FeesBilledInLastFinancialYearChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState {
    last: LastCountEnum;
    months: string[];
    counts: number[];
    currentTotal: number;
    previousTotal: number;
    periodString: string;
    previousPeriodString: string;
    symbol: string;
}

interface TimeFeesData {
    statistics: Statistics;
}

export interface Statistics {
    time: Time;
}

export interface Time {
    feesRecordedInPeriod: FeesRecordedInPeriod;
}

export interface FeesRecordedInPeriod {
    last14Days: Last;
    last30Days: Last;
    last12Months: Last;
    lastWeek: Last;
}

export interface Last {
    summary: Summary;
    timesheetGrouping: TimesheetGrouping[];
}

export interface Summary {
    lastPeriodTotal: number;
    previousPeriodTotal: number;
}

export interface TimesheetGrouping {
    value: number;
    group: string;
}

export const FeesRecordedInPeriodChart: React.FC<FeesBilledInLastFinancialYearChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<TimeFeesData>(ChartData, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        last: LastCountEnum.LAST_WEEK,
        months: [],
        counts: [],
        currentTotal: 0,
        previousTotal: 0,
        periodString: 'in Last 14 Days',
        previousPeriodString: '14 Days',
        symbol: '$',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let months: string[] = [];
                let counts: number[] = [];
                let previousTotal: number = 0;
                let currentTotal: number = 0;
                let periodString: string = '';
                let previousPeriodString: string = '';

                if (!error && data) {
                    if (state.last === LastCountEnum.LAST_WEEK) {
                        var lastWeek = data.statistics.time.feesRecordedInPeriod.lastWeek;
                        months = lastWeek.timesheetGrouping.map((s) => s.group);
                        counts = lastWeek.timesheetGrouping.map((s) => s.value);
                        currentTotal = lastWeek.summary.lastPeriodTotal;
                        previousTotal = lastWeek.summary.previousPeriodTotal;
                        periodString = 'This Week';
                        previousPeriodString = 'Week';
                    } else if (state.last === LastCountEnum.LAST_14_DAYS) {
                        var last14Days = data.statistics.time.feesRecordedInPeriod.last14Days;
                        months = last14Days.timesheetGrouping.map((s) => s.group);
                        counts = last14Days.timesheetGrouping.map((s) => s.value);
                        currentTotal = last14Days.summary.lastPeriodTotal;
                        previousTotal = last14Days.summary.previousPeriodTotal;
                        periodString = 'in Last 14 Days';
                        previousPeriodString = '14 Days';
                    } else if (state.last === LastCountEnum.LAST_30_DAYS) {
                        var last30Days = data.statistics.time.feesRecordedInPeriod.last30Days;
                        months = last30Days.timesheetGrouping.map((s) => s.group);
                        counts = last30Days.timesheetGrouping.map((s) => s.value);
                        currentTotal = last30Days.summary.lastPeriodTotal;
                        previousTotal = last30Days.summary.previousPeriodTotal;
                        periodString = 'in Last 30 Days';
                        previousPeriodString = '30 Days';
                    } else if (state.last === LastCountEnum.LAST_12_MOTNHS) {
                        var last12Months = data.statistics.time.feesRecordedInPeriod.last12Months;
                        months = last12Months.timesheetGrouping.map((s) => s.group);
                        counts = last12Months.timesheetGrouping.map((s) => s.value);
                        currentTotal = last12Months.summary.lastPeriodTotal;
                        previousTotal = last12Months.summary.previousPeriodTotal;
                        periodString = 'in Last 12 Months';
                        previousPeriodString = '12 Months';
                    }
                }

                return {
                    ...prevState,
                    months,
                    counts,
                    previousTotal,
                    currentTotal,
                    periodString,
                    previousPeriodString,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [state.last, loading]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('IsTimesheetFeesInLastPeriodsChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isCurrent: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (state.last === LastCountEnum.LAST_WEEK) {
                // This Week
                searchCriterias.push({ property: 'ThisWeek', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev' });
            } else if (state.last === LastCountEnum.LAST_14_DAYS) {
                searchCriterias.push({ property: 'PeriodByDays', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev', period: '14' });
            } else if (state.last === LastCountEnum.LAST_30_DAYS) {
                searchCriterias.push({ property: 'PeriodByDays', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev', period: '30' });
            } else {
                searchCriterias.push({ property: '12MonthPeriod', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev' });
            }

            searchCriterias.push({ property: 'Amount', operator: 'ne', comparison: '0' });

            props.getSelectedItem(searchCriterias, false, 'Time Recorded', DrillDownType.timesheetSummary, 'Timesheet Lines');
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (loading || currencySymbolQuery.loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }
    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.counts) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.barTitleContainer}>
                <ChartHeading icon={<ReceiptOutlinedIcon />} title={<>Fees Recorded {state.periodString}</>} />
                <div className={`${classes.totalDiv} h2`} onClick={() => handleTotalClick(true)}>
                    <AnimatedNumber value={state.currentTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                    {GetArrow(state.currentTotal, state.previousTotal)}
                </div>
                <div className="h3">
                    <div className={classes.totalDiv} onClick={() => handleTotalClick(false)}>
                        {state.symbol}
                        {formatNumber(state.previousTotal)}
                    </div>
                    in the previous {state.previousPeriodString}
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.months[_index];

                            const searchCriterias = new Array<SearchCriteria>();
                            // var d = new Date();
                            // var year = d.getFullYear();
                            if (state.last === LastCountEnum.LAST_WEEK) {
                                searchCriterias.push({ property: 'Day', operator: 'eq', comparison: item });
                            } else if (state.last === LastCountEnum.LAST_14_DAYS) {
                                searchCriterias.push({ property: 'DateShortString', operator: 'eq', comparison: item });
                            } else if (state.last === LastCountEnum.LAST_30_DAYS) {
                                searchCriterias.push({ property: 'DateShortString', operator: 'eq', comparison: item });
                            } else {
                                searchCriterias.push({ property: 'MonthStart', operator: 'gteq', comparison: item }, { property: 'MonthEnd', operator: 'lteq', comparison: item });
                            }
                            // . e.g. - Apr 22, Apr 23, etc
                            props.getSelectedItem(searchCriterias, false, 'Time Recorded - ' + item, DrillDownType.timesheetSummary, 'Timesheet Lines');
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                fill: false,
                                label: 'Fees Recorded',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.counts,
                                type: 'bar',
                            },
                            // tslint:disable-next-line: no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick(state.counts),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(state.counts),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.counts) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={LastCountEnum.LAST_WEEK} control={<Radio color="primary" />} label="This Week" />
                    <FormControlLabel value={LastCountEnum.LAST_14_DAYS} control={<Radio color="primary" />} label="Last 14 days" />
                    <FormControlLabel value={LastCountEnum.LAST_30_DAYS} control={<Radio color="primary" />} label="Last 30 days" />
                    <FormControlLabel value={LastCountEnum.LAST_12_MOTNHS} control={<Radio color="primary" />} label="Last 12 months" />
                </RadioGroup>
            </FormControl>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

const ChartData = gql`
    query FeesRecordedInPeriodChartData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            time {
                feesRecordedInPeriod(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    last14Days {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        timesheetGrouping {
                            value
                            group
                        }
                    }

                    last30Days {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        timesheetGrouping {
                            value
                            group
                        }
                    }

                    last12Months {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        timesheetGrouping {
                            value
                            group
                        }
                    }

                    lastWeek {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                        }
                        timesheetGrouping {
                            value
                            group
                        }
                    }
                }
            }
        }
    }
`;
